import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, Polygon, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Coordinate, DeliveryPolygon } from '../../types/deliveryPolygons';

interface PolygonDrawerProps {
  initialCoordinates?: Coordinate[];
  onCoordinatesChange: (coordinates: Coordinate[]) => void;
  existingPolygons?: DeliveryPolygon[];
  editingPolygonId?: number | null;
  height?: string;
}

const mapContainerStyle = {
  width: '100%',
};

const defaultCenter = {
  lat: 37.2242,
  lng: 67.2783,
};

const PolygonDrawer: React.FC<PolygonDrawerProps> = ({
  initialCoordinates = [],
  onCoordinatesChange,
  existingPolygons = [],
  editingPolygonId = null,
  height = '400px',
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  const [coordinates, setCoordinates] = useState<Coordinate[]>(initialCoordinates);
  const [mapCenter, setMapCenter] = useState(
    initialCoordinates.length > 0
      ? { lat: initialCoordinates[0].latitude, lng: initialCoordinates[0].longitude }
      : defaultCenter
  );
  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const newCoordinate: Coordinate = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng(),
        };
        const updatedCoordinates = [...coordinates, newCoordinate];
        setCoordinates(updatedCoordinates);
        onCoordinatesChange(updatedCoordinates);
      }
    },
    [coordinates, onCoordinatesChange]
  );

  const handleClearPolygon = () => {
    setCoordinates([]);
    onCoordinatesChange([]);
  };

  const handleUndoLastPoint = () => {
    if (coordinates.length > 0) {
      const updatedCoordinates = coordinates.slice(0, -1);
      setCoordinates(updatedCoordinates);
      onCoordinatesChange(updatedCoordinates);
    }
  };

  const handleRemovePoint = useCallback(
    (indexToRemove: number) => {
      const updatedCoordinates = coordinates.filter((_, index) => index !== indexToRemove);
      setCoordinates(updatedCoordinates);
      onCoordinatesChange(updatedCoordinates);
    },
    [coordinates, onCoordinatesChange]
  );

  const polygonPath = coordinates.map((coord) => ({
    lat: coord.latitude,
    lng: coord.longitude,
  }));

  const polygonOptions = {
    fillColor: '#2196F3',
    fillOpacity: 0.35,
    strokeColor: '#2196F3',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
    geodesic: false,
    zIndex: 1,
  };

  if (!isLoaded) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>{t('polygonDrawer.loadingMap')}</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleUndoLastPoint}
          disabled={coordinates.length === 0}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#ffc107',
            color: '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: coordinates.length === 0 ? 'not-allowed' : 'pointer',
            opacity: coordinates.length === 0 ? 0.5 : 1,
          }}
        >
          {t('polygonDrawer.undoLastPoint', { count: coordinates.length })}
        </button>
        <button
          type="button"
          onClick={handleClearPolygon}
          disabled={coordinates.length === 0}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: coordinates.length === 0 ? 'not-allowed' : 'pointer',
            opacity: coordinates.length === 0 ? 0.5 : 1,
          }}
        >
          {t('polygonDrawer.clearPolygon')}
        </button>
      </div>

      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
        {t('polygonDrawer.instructions')}
        {existingPolygons.length > 0 && (
          <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#ff9800' }}>
            {t('polygonDrawer.existingPolygonsWarning')}
          </div>
        )}
      </div>

      <GoogleMap
        mapContainerStyle={{ ...mapContainerStyle, height }}
        center={mapCenter}
        zoom={13}
        onLoad={onLoad}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          clickableIcons: false,
          disableDefaultUI: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      >
        {/* Show existing polygons in gray */}
        {existingPolygons
          .filter((polygon) => polygon.id !== editingPolygonId)
          .map((polygon) => {
            const path = polygon.coordinates.map((coord) => ({
              lat: coord.latitude,
              lng: coord.longitude,
            }));
            return (
              <Polygon
                key={polygon.id}
                paths={path}
                options={{
                  fillColor: '#9E9E9E',
                  fillOpacity: 0.3,
                  strokeColor: '#9E9E9E',
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  clickable: false,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 0,
                }}
              />
            );
          })}

        {/* Show current polygon being created/edited */}
        {coordinates.length >= 3 && <Polygon paths={polygonPath} options={polygonOptions} />}

        {/* Show markers for current polygon points */}
        {coordinates.map((coord, index) => (
          <Marker
            key={index}
            position={{ lat: coord.latitude, lng: coord.longitude }}
            label={{
              text: (index + 1).toString(),
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#2196F3',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
            onClick={() => handleRemovePoint(index)}
            cursor="pointer"
            title={t('polygonDrawer.removePointTitle', { number: index + 1 })}
          />
        ))}
      </GoogleMap>

      {coordinates.length > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
          {t('polygonDrawer.pointsCount', {
            count: coordinates.length,
            remaining: coordinates.length < 3 ? t('polygonDrawer.needMore', { count: 3 - coordinates.length }) : '',
          })}
        </div>
      )}
    </div>
  );
};

export default PolygonDrawer;
